/**
 * Camera Raw 式基础调整面板（M4-08 / DESIGN.md#65-值得从-photoshop-补充的功能性价比排序）。
 *
 * 七个滑杆：曝光/高光/阴影/白色/黑色/清晰度/去朦胧。交互契约与
 * AdjustmentPanel 里其余控件一致——拖动中连续 onPreview，松手一次 onCommit。
 */
import type { JSX } from 'solid-js';
import { DEFAULT_BASIC_PARAMS, type BasicParams } from '../../engine/adjustments/basic';
import styles from './AdjustmentPanel.module.css';

export interface BasicPanelProps {
  readonly params: Readonly<Record<string, number | readonly number[]>>;
  readonly onPreview: (params: BasicParams) => void;
  readonly onCommit: (params: BasicParams) => void;
}

/** `BasicParams` 没有索引签名；显式标注成 Record，才能塞进 `Adjustment.params`。 */
export function basicParamsToRecord(params: BasicParams): Readonly<Record<string, number>> {
  const record: Readonly<Record<string, number>> = { ...params };
  return record;
}

function readParams(raw: BasicPanelProps['params']): BasicParams {
  const num = (key: keyof BasicParams): number => {
    const v = raw[key];
    return typeof v === 'number' ? v : DEFAULT_BASIC_PARAMS[key];
  };
  return {
    exposure: num('exposure'),
    highlights: num('highlights'),
    shadows: num('shadows'),
    whites: num('whites'),
    blacks: num('blacks'),
    clarity: num('clarity'),
    dehaze: num('dehaze'),
  };
}

const ROWS: ReadonlyArray<{ key: keyof BasicParams; label: string; min: number; max: number; step: number }> = [
  { key: 'exposure', label: '曝光', min: -5, max: 5, step: 0.05 },
  { key: 'highlights', label: '高光', min: -100, max: 100, step: 1 },
  { key: 'shadows', label: '阴影', min: -100, max: 100, step: 1 },
  { key: 'whites', label: '白色', min: -100, max: 100, step: 1 },
  { key: 'blacks', label: '黑色', min: -100, max: 100, step: 1 },
  { key: 'clarity', label: '清晰度', min: -100, max: 100, step: 1 },
  { key: 'dehaze', label: '去朦胧', min: -100, max: 100, step: 1 },
];

export function BasicPanel(props: BasicPanelProps): JSX.Element {
  const params = (): BasicParams => readParams(props.params);

  return (
    <div class={styles.panel} data-testid="basic-panel">
      {ROWS.map((row) => (
        <label class={styles.row}>
          <span class={styles.label}>{row.label}</span>
          <input
            type="range"
            min={row.min}
            max={row.max}
            step={row.step}
            value={params()[row.key]}
            data-testid={`basic-${row.key}`}
            onInput={(event) => {
              props.onPreview({ ...params(), [row.key]: Number(event.currentTarget.value) });
            }}
            onChange={(event) => {
              props.onCommit({ ...params(), [row.key]: Number(event.currentTarget.value) });
            }}
          />
          <span class={styles.value}>{params()[row.key]}</span>
        </label>
      ))}
    </div>
  );
}
